(() => {
  if (window.AtlasProducts) { window.AtlasProducts.init(document); return; }
  const productStates = new Map();
  const recommendationRequests = new Map();
  const stickyRefreshers = new Map();
  const stickyHeights = new Map();
  const publishStickyHeight = (product, height) => {
    if (height === null) stickyHeights.delete(product);
    else stickyHeights.set(product, height);
    const value = `${Math.max(0, ...stickyHeights.values())}px`;
    if (document.documentElement.style.getPropertyValue('--atlas-sticky-height') !== value) document.documentElement.style.setProperty('--atlas-sticky-height', value);
  };
  const find = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const requestHTML = async (url, controller, headers = {}) => {
    let timer;
    let timedOut = false;
    let onAbort;
    const deadline = new Promise((_, reject) => {
      onAbort = () => { if (!timedOut) reject(new DOMException('Request cancelled', 'AbortError')); };
      controller.signal.addEventListener('abort', onAbort, { once: true });
      timer = setTimeout(() => {
        timedOut = true;
        const error = new Error('Product request timed out');
        error.atlasTimedOut = true;
        reject(error);
        controller.abort();
      }, 10000);
    });
    const operation = (async () => {
      const response = await fetch(url, { signal: controller.signal, headers });
      if (!response.ok) throw new Error('Product update failed');
      return response.text();
    })();
    try { return await Promise.race([operation, deadline]); }
    finally { clearTimeout(timer); controller.signal.removeEventListener('abort', onAbort); }
  };
  const initializeProduct = (product) => {
    if (product.dataset.productInitialized) return;
    product.dataset.productInitialized = 'true';
    const lifecycle = new AbortController();
    let disposed = false;
    let activeRequest;
    let pickupRequest;
    let stickyObserver;
    let stickySizeObserver;
    let observedFooter;
    let lastChangedInput;
    const gallery = product.querySelector('[data-product-gallery]');
    if (gallery) gallery.classList.add('is-enhanced');
    const galleryItems = gallery ? [...gallery.querySelectorAll('[data-product-media]')].filter((item) => item.dataset.productMedia && (!item.dataset.mediaType || ['image', 'video', 'external_video', 'model'].includes(item.dataset.mediaType))) : [];
    let swipe;
    let suppressZoomUntil = 0;
    let cancelGalleryGesture = () => {};
    let mediaAnimation;
    let mediaAnimationImage;
    let removePendingMediaLoad;
    let mediaMotionGeneration = 0;
    const motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const cancelMediaTransition = () => {
      mediaMotionGeneration++;
      removePendingMediaLoad?.();
      removePendingMediaLoad = undefined;
      mediaAnimation?.cancel();
      mediaAnimation = undefined;
      mediaAnimationImage?.classList.remove('is-media-entering');
      mediaAnimationImage = undefined;
    };
    const mediaMotionAllowed = () => gallery?.dataset.mediaTransition === 'true' && document.body.dataset.animations !== 'false' && !motionPreference?.matches;
    const transitionMediaImage = (selected, previous) => {
      cancelMediaTransition();
      if (!mediaMotionAllowed() || selected.dataset.mediaType !== 'image' || previous?.dataset.mediaType !== 'image') return;
      const image = selected.querySelector('.product-media__image');
      if (!image || typeof image.animate !== 'function' || (image.complete && !image.naturalWidth)) return;
      const generation = mediaMotionGeneration;
      const direction = (galleryItems.indexOf(selected) > galleryItems.indexOf(previous) ? 1 : -1) * (isRTL() ? -1 : 1);
      mediaAnimationImage = image;
      image.classList.add('is-media-entering');
      const start = () => {
        if (disposed || generation !== mediaMotionGeneration) return;
        removePendingMediaLoad?.();
        removePendingMediaLoad = undefined;
        if (!mediaMotionAllowed() || !selected.classList.contains('is-active') || !image.naturalWidth) { cancelMediaTransition(); return; }
        try {
          const animation = image.animate([
            { opacity: 0, transform: `translateX(${direction * 8}px)` },
            { opacity: 1, transform: 'translateX(0)' }
          ], { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both' });
          mediaAnimation = animation;
          animation.finished.then(() => {
            if (generation === mediaMotionGeneration) cancelMediaTransition();
          }, () => {
            if (generation === mediaMotionGeneration) cancelMediaTransition();
          });
        } catch { cancelMediaTransition(); }
      };
      if (image.complete) start();
      else {
        const onError = () => { if (generation === mediaMotionGeneration) cancelMediaTransition(); };
        image.addEventListener('load', start, { once: true, signal: lifecycle.signal });
        image.addEventListener('error', onError, { once: true, signal: lifecycle.signal });
        removePendingMediaLoad = () => {
          image.removeEventListener('load', start);
          image.removeEventListener('error', onError);
        };
      }
    };
    motionPreference?.addEventListener?.('change', () => { if (motionPreference.matches) cancelMediaTransition(); }, { signal: lifecycle.signal });
    const isRTL = () => gallery && getComputedStyle(gallery).direction === 'rtl';
    const syncGalleryNavigation = (announce = false) => {
      if (!gallery) return;
      const index = galleryItems.findIndex((item) => item.classList.contains('is-active'));
      const buttons = [...gallery.querySelectorAll('[data-media-step]')];
      const focused = document.activeElement;
      buttons.forEach((button) => {
        button.disabled = index < 0 || (Number(button.dataset.mediaStep) < 0 ? index === 0 : index === galleryItems.length - 1);
        button.setAttribute('aria-disabled', String(button.disabled));
      });
      if (buttons.includes(focused) && focused.disabled) buttons.find((button) => !button.disabled)?.focus({ preventScroll: true });
      const status = gallery.querySelector('[data-media-status]');
      if (announce && status && index >= 0) status.textContent = (gallery.dataset.mediaStatusLabel || '').replace('[current]', String(index + 1)).replace('[total]', String(galleryItems.length));
    };
    const models = product.querySelector('[data-product-models]');
    if (models && window.Shopify?.loadFeatures) {
      const setupXR = () => {
        if (disposed) return;
        if (!window.ShopifyXR) { document.addEventListener('shopify_xr_initialized', setupXR, { once: true, signal: lifecycle.signal }); return; }
        window.ShopifyXR.addModels(JSON.parse(models.textContent));
        window.ShopifyXR.setupXRElements();
      };
      window.Shopify.loadFeatures([
        { name: 'shopify-xr', version: '1.0', onLoad: setupXR },
        { name: 'model-viewer-ui', version: '1.0', onLoad: (error) => { if (!disposed && !error && window.Shopify.ModelViewerUI) product.querySelectorAll('model-viewer').forEach((model) => { model.atlasViewer = new window.Shopify.ModelViewerUI(model); }); } }
      ]);
    }
    const activateMedia = (mediaId, announce = false) => {
      if (!mediaId || !gallery) return;
      const selected = galleryItems.find((item) => item.dataset.productMedia === mediaId);
      if (!selected) return;
      const previous = galleryItems.find((item) => item.classList.contains('is-active'));
      if (swipe && swipe.image !== selected) cancelGalleryGesture();
      gallery.querySelectorAll('[data-product-media]').forEach((item) => {
        const isActive = item === selected;
        if (!isActive && item.classList.contains('is-active')) {
          item.querySelectorAll('video').forEach((video) => video.pause());
          item.querySelectorAll('iframe').forEach((iframe) => { iframe.src = iframe.src; });
          item.querySelectorAll('model-viewer').forEach((model) => { model.atlasViewer?.pause(); model.pause?.(); });
        }
        item.classList.toggle('is-active', isActive);
      });
      if (selected !== previous) transitionMediaImage(selected, previous);
      gallery.querySelectorAll('[data-media-target]').forEach((button) => button.setAttribute('aria-current', String(button.dataset.mediaTarget === mediaId)));
      const thumbnail = gallery.querySelector(`[data-media-target="${CSS.escape(mediaId)}"]`);
      const strip = gallery.querySelector('[data-media-thumbnails]');
      if (thumbnail && strip) {
        const itemRect = thumbnail.getBoundingClientRect();
        const stripRect = strip.getBoundingClientRect();
        if (itemRect.left < stripRect.left) strip.scrollBy({ left: itemRect.left - stripRect.left, behavior: 'instant' });
        else if (itemRect.right > stripRect.right) strip.scrollBy({ left: itemRect.right - stripRect.right, behavior: 'instant' });
      }
      syncGalleryNavigation(announce);
    };
    const stepMedia = (step) => {
      const index = galleryItems.findIndex((item) => item.classList.contains('is-active'));
      const item = galleryItems[index + step];
      if (index >= 0 && item) activateMedia(item.dataset.productMedia, true);
    };
    if (gallery) {
      const dragEnabled = () => gallery.dataset.mobileSwipe === 'true';
      gallery.querySelectorAll('[data-media-type="image"] img').forEach((image) => { image.draggable = false; });
      const resetGesture = () => {
        const gesture = swipe;
        swipe = undefined;
        gallery.classList.remove('is-dragging');
        try { if (gesture?.captureTarget?.hasPointerCapture?.(gesture.id)) gesture.captureTarget.releasePointerCapture(gesture.id); } catch {}
      };
      cancelGalleryGesture = (suppressClick = false) => {
        if (swipe && (swipe.moved || suppressClick)) suppressZoomUntil = performance.now() + 450;
        resetGesture();
      };
      gallery.addEventListener('pointerdown', (event) => {
        if (swipe || event.isPrimary === false) { cancelGalleryGesture(true); return; }
        const image = event.target.closest('[data-product-media].is-active[data-media-type="image"]');
        if (!dragEnabled() || galleryItems.length < 2 || !['touch', 'pen', 'mouse'].includes(event.pointerType) || (event.pointerType === 'mouse' && (event.button !== 0 || event.ctrlKey)) || !image || gallery.querySelector('[data-image-dialog][open]') || event.target.closest('input, select, textarea, a, video, iframe, model-viewer, [data-shopify-xr], [data-close-image], .product-media__zoom-label, [contenteditable], [inert], [disabled]') || (event.target.closest('button') && !event.target.closest('[data-media-zoom]'))) return;
        suppressZoomUntil = 0;
        swipe = { id: event.pointerId, type: event.pointerType, x: event.clientX, y: event.clientY, image, captureTarget: event.target.closest('[data-media-zoom]') || image, vertical: false, moved: false, captured: false };
      }, { passive: true, signal: lifecycle.signal });
      window.addEventListener('pointerdown', (event) => {
        if (swipe && event.pointerId !== swipe.id) cancelGalleryGesture(true);
      }, { passive: true, signal: lifecycle.signal });
      window.addEventListener('pointermove', (event) => {
        if (!swipe || event.pointerId !== swipe.id) return;
        if (swipe.type === 'mouse' && event.buttons === 0) { cancelGalleryGesture(); return; }
        const x = Math.abs(event.clientX - swipe.x);
        const y = Math.abs(event.clientY - swipe.y);
        if (Math.max(x, y) > 8) swipe.moved = true;
        if (y > 12 && y >= x) swipe.vertical = true;
        if (swipe.vertical || x <= 8 || x < y * 1.4) return;
        gallery.classList.add('is-dragging');
        if (swipe.type === 'mouse' && !swipe.captured) {
          try { swipe.captureTarget.setPointerCapture?.(swipe.id); swipe.captured = Boolean(swipe.captureTarget.hasPointerCapture?.(swipe.id)); } catch {}
        }
      }, { passive: true, signal: lifecycle.signal });
      window.addEventListener('pointerup', (event) => {
        const gesture = swipe;
        if (!gesture || event.pointerId !== gesture.id) return;
        resetGesture();
        const x = event.clientX - gesture.x;
        const y = event.clientY - gesture.y;
        if (gesture.moved || Math.max(Math.abs(x), Math.abs(y)) > 8) suppressZoomUntil = performance.now() + 450;
        if (gesture.vertical || !dragEnabled() || !gesture.image.classList.contains('is-active') || gallery.querySelector('[data-image-dialog][open]')) return;
        if (Math.abs(x) < 45 || Math.abs(x) < Math.abs(y) * 1.4) return;
        stepMedia((x < 0 ? 1 : -1) * (isRTL() ? -1 : 1));
      }, { passive: true, signal: lifecycle.signal });
      window.addEventListener('pointercancel', (event) => {
        if (swipe && event.pointerId === swipe.id) cancelGalleryGesture(true);
      }, { passive: true, signal: lifecycle.signal });
      gallery.addEventListener('lostpointercapture', (event) => {
        if (swipe && event.pointerId === swipe.id) cancelGalleryGesture(true);
      }, { signal: lifecycle.signal });
      gallery.addEventListener('dragstart', (event) => {
        if (event.target.closest('[data-product-media][data-media-type="image"]')) event.preventDefault();
      }, { signal: lifecycle.signal });
      window.addEventListener('blur', () => cancelGalleryGesture(), { signal: lifecycle.signal });
      syncGalleryNavigation();
    }
    const updateSticky = () => {
      if (disposed) return;
      const sticky = product.querySelector('[data-sticky-purchase]');
      const purchase = product.querySelector('[data-purchase-region]');
      if (!sticky || !purchase) { publishStickyHeight(product, 0); return; }
      const purchaseRect = purchase.getBoundingClientRect();
      const footerRect = observedFooter?.isConnected ? observedFooter.getBoundingClientRect() : null;
      const footerVisible = footerRect && footerRect.height > 0 && footerRect.top <= window.innerHeight && footerRect.bottom >= 0;
      sticky.hidden = !(purchaseRect.bottom < 0) || Boolean(footerVisible);
      publishStickyHeight(product, sticky.hidden ? 0 : Math.ceil(sticky.getBoundingClientRect().height));
    };
    const observePurchase = (excludedRoot) => {
      stickyObserver?.disconnect();
      stickySizeObserver?.disconnect();
      observedFooter = document.querySelector('.site-footer');
      if (excludedRoot?.contains(observedFooter)) observedFooter = undefined;
      const purchase = product.querySelector('[data-purchase-region]');
      const sticky = product.querySelector('[data-sticky-purchase]');
      if (purchase && sticky) {
        stickyObserver = new IntersectionObserver(updateSticky, { threshold: 0 });
        stickyObserver.observe(purchase);
        if (observedFooter) stickyObserver.observe(observedFooter);
        if (typeof ResizeObserver === 'function') {
          stickySizeObserver = new ResizeObserver(updateSticky);
          stickySizeObserver.observe(sticky);
        }
      }
      updateSticky();
    };
    window.addEventListener('resize', updateSticky, { passive: true, signal: lifecycle.signal });
    const updatePickup = async () => {
      pickupRequest?.abort();
      const container = product.querySelector('[data-pickup-container]');
      const variantId = product.dataset.variantId;
      if (!container || !variantId) return;
      const controller = new AbortController();
      pickupRequest = controller;
      try {
        const root = container.dataset.root || '/';
        const contentHTML = await requestHTML(`${root.replace(/\/?$/, '/')}variants/${encodeURIComponent(variantId)}/?section_id=pickup-availability`, controller);
        const html = new DOMParser().parseFromString(contentHTML, 'text/html');
        if (pickupRequest !== controller) return;
        const content = html.querySelector('[data-pickup-content]');
        if (content && variantId === product.dataset.variantId) container.innerHTML = content.innerHTML;
      } catch { }
    };
    const updateVariant = async (target) => {
      lastChangedInput = target;
      const optionValues = [...product.querySelectorAll('[data-option-input]:checked, [data-option-select]')].map((input) => input.value).filter(Boolean);
      const linkedUrl = target.matches('[data-option-select]') ? target.selectedOptions[0]?.dataset.productUrl : target.dataset.productUrl;
      const currentUrl = new URL(product.dataset.productUrl, location.origin);
      const nextUrl = new URL(linkedUrl || currentUrl, location.origin);
      if (nextUrl.pathname !== currentUrl.pathname) {
        nextUrl.searchParams.set('option_values', optionValues.join(','));
        location.assign(nextUrl.href);
        return;
      }
      nextUrl.searchParams.set('section_id', product.dataset.sectionId);
      if (optionValues.length) nextUrl.searchParams.set('option_values', optionValues.join(','));
      else if (product.dataset.variantId) nextUrl.searchParams.set('variant', product.dataset.variantId);
      const sellingPlan = product.querySelector('[data-selling-plan]')?.value;
      if (sellingPlan) nextUrl.searchParams.set('selling_plan', sellingPlan);
      activeRequest?.abort();
      const controller = new AbortController();
      activeRequest = controller;
      const quantity = product.querySelector('[name="quantity"]')?.value;
      const focusId = target.id;
      const optionIndex = [...product.querySelectorAll('[data-option-select]')].indexOf(target);
      const purchase = product.querySelector('[data-purchase-region]');
      product.querySelectorAll('[data-variant-error], [data-retry-variant]').forEach((element) => { element.hidden = true; });
      if (purchase) purchase.inert = true;
      product.setAttribute('aria-busy', 'true');
      product.querySelectorAll('[data-add-button], [data-sticky-submit], [data-buy-now]').forEach((button) => { button.disabled = true; });
      try {
        const contentHTML = await requestHTML(nextUrl.href, controller, { 'X-Requested-With': 'XMLHttpRequest' });
        const html = new DOMParser().parseFromString(contentHTML, 'text/html');
        if (activeRequest !== controller) return;
        const nextProduct = html.querySelector(`[data-atlas-product][data-section-id="${CSS.escape(product.dataset.sectionId)}"]`);
        if (!nextProduct) throw new Error('Product section not found');
        const replacements = [...product.querySelectorAll('[data-variant-region]')].map((region) => {
          const nextRegion = nextProduct.querySelector(`[data-variant-region="${CSS.escape(region.dataset.variantRegion)}"]`);
          if (!nextRegion) throw new Error('Incomplete product section');
          return { region, nextRegion };
        });
        const currentForm = product.querySelector('[data-product-form]');
        const nextVariantInput = nextProduct.querySelector('[data-product-form] input[name="id"]');
        if (currentForm && (!nextVariantInput || nextVariantInput.value !== (nextProduct.dataset.variantId || ''))) throw new Error('Inconsistent product variant');
        replacements.forEach(({ region, nextRegion }) => region.replaceWith(document.importNode(nextRegion, true)));
        product.dataset.variantId = nextProduct.dataset.variantId || '';
        product.dataset.featuredMedia = nextProduct.dataset.featuredMedia || '';
        const quantityInput = product.querySelector('[name="quantity"]');
        if (quantityInput && quantity) {
          const minimum = Number(quantityInput.min) || 1;
          const maximum = quantityInput.max ? Number(quantityInput.max) : Infinity;
          const step = Number(quantityInput.step) || 1;
          const boundedQuantity = Math.min(Number(quantity) || minimum, maximum);
          quantityInput.value = String(minimum + Math.max(0, Math.floor((boundedQuantity - minimum) / step)) * step);
        }
        const pageUrl = new URL(location.href);
        pageUrl.searchParams.delete('option_values');
        if (product.dataset.variantId) pageUrl.searchParams.set('variant', product.dataset.variantId);
        else { pageUrl.searchParams.delete('variant'); pageUrl.searchParams.set('option_values', optionValues.join(',')); }
        if (sellingPlan && product.querySelector('[data-selling-plan]')?.value === sellingPlan) pageUrl.searchParams.set('selling_plan', sellingPlan);
        else pageUrl.searchParams.delete('selling_plan');
        history.replaceState({}, '', pageUrl.href);
        const focused = focusId ? product.querySelector(`#${CSS.escape(focusId)}`) : product.querySelectorAll('[data-option-select]')[optionIndex];
        focused?.focus({ preventScroll: true });
        activateMedia(product.dataset.featuredMedia);
        observePurchase();
        updatePickup();
        window.Shopify?.PaymentButton?.init();
        try { window.AtlasCurrency?.refresh?.(product); } catch {}
        const status = product.querySelector('[data-variant-status]');
        if (status) status.textContent = product.querySelector('.product-pricing')?.textContent.trim() || '';
        product.dispatchEvent(new CustomEvent('atlas:variant-change', { bubbles: true, detail: { variantId: product.dataset.variantId } }));
      } catch (error) {
        if (activeRequest !== controller || error.name === 'AbortError') return;
        const variantInput = product.querySelector('input[name="id"]');
        if (variantInput) variantInput.disabled = true;
        const errorElement = product.querySelector('[data-variant-error]');
        if (errorElement) { errorElement.textContent = product.dataset.updateError; errorElement.hidden = false; }
        const retryButton = product.querySelector('[data-retry-variant]');
        if (retryButton) retryButton.hidden = false;
        product.querySelector('.product-form__express')?.setAttribute('hidden', '');
      } finally {
        if (activeRequest === controller) {
          product.removeAttribute('aria-busy');
          const currentPurchase = product.querySelector('[data-purchase-region]');
          if (currentPurchase) currentPurchase.inert = false;
        }
      }
    };
    product.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input], [data-option-select], [data-selling-plan]')) updateVariant(event.target);
    }, { signal: lifecycle.signal });
    product.addEventListener('click', async (event) => {
      if (event.target.closest('[data-retry-variant]')) {
        const input = lastChangedInput?.isConnected ? lastChangedInput : product.querySelector('[data-option-input]:checked, [data-option-select], [data-selling-plan]');
        if (input) updateVariant(input);
      }
      const thumbnail = event.target.closest('[data-media-target]');
      if (thumbnail) activateMedia(thumbnail.dataset.mediaTarget, true);
      const galleryButton = event.target.closest('[data-media-step]');
      if (galleryButton && !galleryButton.disabled) stepMedia(Number(galleryButton.dataset.mediaStep));
      const quantityButton = event.target.closest('[data-product-quantity-step]');
      if (quantityButton) {
        const input = quantityButton.closest('.product-quantity')?.querySelector('input');
        if (input) { if (!input.value) input.value = input.min || '1'; Number(quantityButton.dataset.productQuantityStep) > 0 ? input.stepUp() : input.stepDown(); input.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      const stickyButton = event.target.closest('[data-sticky-submit]');
      if (stickyButton) {
        const form = product.querySelector('[data-product-form]');
        const button = form?.querySelector('[data-add-button]');
        if (form && button && !button.disabled) form.requestSubmit(button);
      }
      const zoomButton = event.target.closest('[data-media-zoom]');
      const dialog = product.querySelector('[data-image-dialog]');
      if (zoomButton && dialog?.showModal) {
        if (event.detail > 0 && performance.now() < suppressZoomUntil) { event.preventDefault(); return; }
        const image = dialog.querySelector('[data-zoom-image]');
        image.src = zoomButton.dataset.imageUrl;
        image.alt = zoomButton.dataset.imageAlt || '';
        dialog.showModal();
      }
      if (event.target.closest('[data-close-image]') || event.target === dialog) dialog?.close();
      const share = event.target.closest('[data-share-product]');
      if (share) {
        const status = product.querySelector('[data-share-status]');
        const url = new URL(location.href);
        url.searchParams.delete('section_id');
        try {
          if (navigator.share) await navigator.share({ title: share.dataset.shareTitle, url: url.href });
          else if (navigator.clipboard) { await navigator.clipboard.writeText(url.href); if (status) status.textContent = share.dataset.shareSuccess; }
          else if (status) status.textContent = url.href;
        } catch (error) { if (error.name !== 'AbortError' && status) status.textContent = url.href; }
      }
    }, { signal: lifecycle.signal });
    product.addEventListener('keydown', (event) => {
      const thumbnail = event.target.closest('[data-media-target]');
      const galleryButton = event.target.closest('[data-media-step]');
      if ((!thumbnail && !galleryButton) || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const thumbnails = [...gallery.querySelectorAll('[data-media-target]')].filter((button) => galleryItems.some((item) => item.dataset.productMedia === button.dataset.mediaTarget));
      let index = galleryButton ? galleryItems.findIndex((item) => item.classList.contains('is-active')) : thumbnails.indexOf(thumbnail);
      const count = galleryButton ? galleryItems.length : thumbnails.length;
      if (index < 0 || !count) return;
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = count - 1;
      else index = Math.max(0, Math.min(count - 1, index + (event.key === 'ArrowRight' ? 1 : -1) * (isRTL() ? -1 : 1)));
      event.preventDefault();
      if (thumbnail) thumbnails[index].focus({ preventScroll: true });
      activateMedia(galleryButton ? galleryItems[index].dataset.productMedia : thumbnails[index].dataset.mediaTarget, true);
    }, { signal: lifecycle.signal });
    productStates.set(product, () => {
      disposed = true;
      cancelGalleryGesture();
      cancelMediaTransition();
      lifecycle.abort();
      activeRequest?.abort();
      activeRequest = undefined;
      pickupRequest?.abort();
      pickupRequest = undefined;
      stickyObserver?.disconnect();
      stickySizeObserver?.disconnect();
      stickyRefreshers.delete(product);
      publishStickyHeight(product, null);
      delete product.dataset.productInitialized;
      productStates.delete(product);
    });
    stickyRefreshers.set(product, observePurchase);
    observePurchase();
    updatePickup();
  };
  const initializeRecommendations = async (recommendations) => {
    if (recommendations.dataset.loaded) return;
    recommendations.dataset.loaded = 'true';
    const controller = new AbortController();
    recommendationRequests.set(recommendations, controller);
    try {
      const contentHTML = await requestHTML(recommendations.dataset.url, controller);
      const html = new DOMParser().parseFromString(contentHTML, 'text/html');
      if (controller.signal.aborted || !recommendations.isConnected || recommendationRequests.get(recommendations) !== controller) return;
      const updated = html.querySelector('[data-product-recommendations]');
      if (updated && !updated.hidden && updated.querySelector('.product-card')) {
        recommendations.innerHTML = updated.innerHTML;
        recommendations.hidden = false;
        recommendations.dispatchEvent(new CustomEvent('atlas:products-rendered', { bubbles: true }));
        window.AtlasProductCarousel?.init(recommendations);
      }
    } catch {
      if (!controller.signal.aborted && recommendations.isConnected && recommendationRequests.get(recommendations) === controller && !window.Shopify?.designMode) recommendations.hidden = true;
    } finally {
      if (recommendationRequests.get(recommendations) === controller) recommendationRequests.delete(recommendations);
    }
  };
  const initialize = (root = document) => {
    find(root, '[data-atlas-product]').forEach(initializeProduct);
    find(root, '[data-product-recommendations]').forEach(initializeRecommendations);
  };
  window.AtlasProducts = { init: initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    if (find(event.target, '.site-footer').length) for (const refresh of stickyRefreshers.values()) refresh();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [product, dispose] of productStates) if (event.target.contains(product)) dispose();
    find(event.target, '[data-product-recommendations]').forEach((recommendations) => {
      recommendationRequests.get(recommendations)?.abort();
      recommendationRequests.delete(recommendations);
      delete recommendations.dataset.loaded;
    });
    if (find(event.target, '.site-footer').length) for (const refresh of stickyRefreshers.values()) refresh(event.target);
  });
})();
